import AdbIcon from "@mui/icons-material/Adb";
import MenuIcon from "@mui/icons-material/Menu";
import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import * as React from "react";
import { Link } from "react-router-dom";
import LoginForm from "@components/LoginForm.jsx";
import { LeftPanelMobile } from "@components/LeftPanelMobile";
import Drawer from "@mui/material/Drawer";

import { logout, getCurrentUser } from "@helpers/api";

import useAPI from "@stores/api";
import usePrivacy from "@stores/privacy";
import ConditionalRender from "@components/ConditionalRender";

const pages = [
  { name: "Búsqueda", path: "/" },
  { name: "Contacto", path: "/about" },
  {
    name: "Documentación",
    path: "/docs/site/",
    requiresAuth: true,
    external: true,
  },
];

const settings = {
  ADMINPANEL: { text: "Panel de Administración", path: "/admin" },
  CHECKS: { text: "Comprobaciones", path: "/checks" },
  LOGOUT: { text: "Cerrar sesión" },
};

function ResponsiveAppBar() {
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const loginForm = React.useRef(null);
  const privacyWarnOpen = usePrivacy((state) => state.warnOpen);
  const setPrivacyWarnOpen = usePrivacy((state) => state.setWarnOpen);

  const token = useAPI((state) => state.token);
  const [isAdmin, setIsAdmin] = React.useState(false);

  React.useEffect(() => {
    if (!token) {
      setIsAdmin(false);
      return;
    }
    // Infer admin from token payload immediately for navigation responsiveness
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const payload = JSON.parse(atob(base64));
      setIsAdmin(payload && payload.sub === "admin");
    } catch (_) {
      setIsAdmin(false);
    }
  }, [token]);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event) => {
    if (token) {
      setAnchorElUser(event.currentTarget);
    } else {
      loginForm.current.openModal();
    }
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleSelectionUserMenu = (e, setting) => {
    handleCloseUserMenu();

    switch (setting) {
      case settings.ADMINPANEL:
        break;
      case settings.CHECKS:
        break;
      case settings.LOGOUT:
        logout();
        break;
      default:
        console.error("User settings: Uknown option");
    }
  };

  const specialFunctions = [
    {
      name: "Privacidad",
      function: (e) => setPrivacyWarnOpen(!privacyWarnOpen),
    },
    ...(isAdmin
      ? [
          {
            name: "Checks",
            function: (e) => {
              window.location.href = "/checks";
            },
          },
        ]
      : []),
  ];

  const logo = (
    <div
      onClick={() => {
        window.location.href = "/";
      }}
    >
      <h1 className="rightTitle">
        <span className="titlecolor">Repo</span>Search
      </h1>
    </div>
  );

  const drawer = <LeftPanelMobile handleCloseNavMenu={handleCloseNavMenu} />;

  return (
    <div>
      <AppBar position="static" color="inherit">
        <Container maxWidth="l">
          <Toolbar disableGutters>
            <Typography
              variant="h6"
              noWrap
              component="a"
              sx={{
                mr: 2,
                display: { xs: "none", lg: "flex" },
                fontFamily: "monospace",
                fontWeight: 700,
                letterSpacing: ".3rem",
                color: "inherit",
                textDecoration: "none",
              }}
            >
              {logo}
            </Typography>

            <Box sx={{ flexGrow: 1, display: { xs: "flex", lg: "none" } }}>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="primary"
              >
                <MenuIcon />
              </IconButton>
              <Drawer
                variant="temporary"
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                ModalProps={{
                  keepMounted: true, // Better open performance on mobile.
                }}
                sx={{
                  display: { xs: "block", lg: "none" },
                  "& .MuiDrawer-paper": { boxSizing: "border-box" },
                }}
              >
                {drawer}
              </Drawer>
            </Box>
            <Typography
              variant="h9"
              noWrap
              component="a"
              sx={{
                mr: 2,
                display: { xs: "flex", lg: "none" },
                flexGrow: 1,
                fontFamily: "monospace",
                fontWeight: 700,
                letterSpacing: ".3rem",
                color: "inherit",
                textDecoration: "none",
              }}
            >
              {logo}
            </Typography>
            <Box sx={{ flexGrow: 1, display: { xs: "none", lg: "flex" } }}>
              {pages.map((page) => (
                <ConditionalRender
                  key={page.name}
                  condition={
                    page.requiresAuth ? localStorage.getItem("token") : true
                  }
                >
                  <Link to={page.path} reloadDocument={page.external}>
                    <Button
                      onClick={handleCloseNavMenu}
                      sx={{ my: 2, display: "block" }}
                    >
                      {page.name}
                    </Button>
                  </Link>
                </ConditionalRender>
              ))}
              {specialFunctions.map((special) => (
                <div onClick={special.function} key={special.name}>
                  <Button
                    onClick={handleCloseNavMenu}
                    sx={{ my: 2, display: "block" }}
                  >
                    {special.name}
                  </Button>
                </div>
              ))}
            </Box>

            <Box sx={{ flexGrow: 0 }}>
              <Tooltip title="Abrir ajustes">
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                  <Avatar />
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: "45px" }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                {Object.values(settings).map((setting) => (
                  <MenuItem
                    key={setting.text}
                    onClick={(e) => handleSelectionUserMenu(e, setting)}
                  >
                    {setting.path ? (
                      <Link
                        to={setting.path}
                        style={{ textDecoration: "none", color: "unset" }}
                      >
                        <Typography textAlign="center">
                          {setting.text}
                        </Typography>
                      </Link>
                    ) : (
                      <Typography textAlign="center">{setting.text}</Typography>
                    )}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      <LoginForm ref={loginForm} />
    </div>
  );
}
export default ResponsiveAppBar;
